import React from "react";
import {
  Box,
  Flex,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Button,
  Badge,
  Icon,
  HStack,
  VStack,
  Container,
} from "@chakra-ui/react";
import {
  Card, CardHeader, CardContent, CardTitle, Right
} from "../../../../components/Card";
import { ArrowRightIcon } from "@chakra-ui/icons";
import { FaTasks, FaCalendarAlt } from "react-icons/fa";
import { TaskState } from "../../../../components/States";
import { translateKind } from "../../../../shared/libs/translate";
import Link from "../../../../components/Link";
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import greenToRed from "../../../../helpers/GreenToRed";
import formatDate from "../../../../helpers/formatDate";
import formatDateTable from "../../../../helpers/FormatDateTable";
import Pagination from "../../../../components/pagination/Pagination";
import TaskFilterBar from "./TaskFilterBar";
import { useTranslation } from 'react-i18next';

export default function TaskListTableView({ tasks, onRefresh, title = "Alt Görevler", icon = FaTasks, paginationPath = "gorevler" }) {
  const { t } = useTranslation();
  const bgGradient = "linear(135deg, white 0%, blue.50 100%)";
  const headerBg = "linear(135deg, red.600 0%, red.900 100%)";
  const cardBg = "white";
  const tableBg = "gray.50";
  const borderColor = "gray.200";
  
  const hoverBg = "gray.50";
  const columns = [
    {
      title: t('task.type'),
      dataIndex: "kind",
      render: (text) => <Text>{translateKind(text)}</Text>,
    },
    {
      title: t('task.title'),
      dataIndex: "title",
      render: (text, record) => (
        <Box maxWidth={"400px"}>
          <Link
            to={`/is-emri/${urlToId(record.ticket) || ""}?gorev=${
              urlToId(record.url) || ""
            }`}
          >
            <Text as="u">{text}</Text>
          </Link>
        </Box>
      ),
    },
    {
      title: t('task.team'),
      dataIndex: "assigned_to",
      render: (text) => <Text>{text ? text.name : "--"}</Text>,
    },
    {
      title: t('task.created_date'),
      dataIndex: "created_at",
      render: (text) => {
        return formatDateTable(text);
      },
    },
    {
      title: t('task.appointment'),
      dataIndex: "appointment_date",
      render: (_, task) => {
        if (!task.appointment_date) return;
        return (
          <Flex flexDirection="column" textAlign="center">
            <Text as="span">{formatDateTable(task.appointment_date)}</Text>{" "}
            <Text as="span">
              {task.appointment_start_time?.slice(0, -3)} -{" "}
              {task.appointment_end_time?.slice(0, -3)}
            </Text>
          </Flex>
        );
      },
    },
    {
      title: t('task.status'),
      dataIndex: "state",
      render: (state) => <TaskState state={state} />,
    },
    {
      title: t('task.progress_expectation'),
      dataIndex: "deadline_at",
      render: (deadline_at, { deadline_ratio }) =>
        deadline_ratio ? (
          <Text color={greenToRed(deadline_ratio)} wrap="nowrap">
            {formatDate(deadline_at)}
          </Text>
        ) : (
          "--"
        ),
    },
  ];

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      p={6}
    >
      <Container maxW="7xl">
        {/* Header */}
        <Box
          bgGradient={headerBg}
          borderRadius="xl"
          p={6}
          mb={6}
          shadow="md"
        >
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Icon as={icon} boxSize={8} color="white" />
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="bold" color="white">
                  {title}
                </Text>
                <Text fontSize="sm" color="white">
                  {t('task.total_tasks', { count: tasks.num_of_items })}
                </Text>
              </VStack>
            </HStack>
            <Button
              leftIcon={<ArrowRightIcon />}
              variant="subtle"
              size="sm"
              onClick={onRefresh}
              isDisabled={!onRefresh}
            >
              {t('common.refresh')}
            </Button>
          </Flex>
        </Box>

        {/* Content Area */}
        <Box
          bg={cardBg}
          borderRadius="xl"
          shadow="lg"
          border="1px"
          borderColor={borderColor}
          overflow="hidden"
        >
          <TableContainer>
            <Table size="md">
              <Thead bg={tableBg}>
                <Tr>
                  {columns.map((column) => (
                    <Th
                      key={column.dataIndex}
                      py={4}
                      px={6}
                      fontSize="xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      color="gray.600"
                      borderColor={borderColor}
                    >
                      {column.title}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {tasks.results.map((t) => (
                  <Tr
                    key={t.url}
                    _hover={{
                      bg: hoverBg
                    }}
                    transition="all 0.2s ease"
                  >
                    {columns.map((column) => (
                      <Td
                        key={column.dataIndex}
                        py={4}
                        px={6}
                        borderColor={borderColor}
                      >
                        {column.render ? (
                          column.render(t[column.dataIndex], t)
                        ) : (
                          <Text>{t[column.dataIndex]}</Text>
                        )}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          <Box p={4} borderTop="1px" borderColor={borderColor}>
            <Pagination
              path={paginationPath}
              num_of_items={tasks.num_of_items}
              num_of_pages={tasks.num_of_pages}
              currentPage={tasks.current_page}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export async function AppointmentsLoader() {
  const resp = await client.get(
    "/tasks/?has_appointment=true&ordering=-appointment_date&state=approval&state=assignment&state=in_progress&state=in_queue"
  );
  return resp.data;
}
